import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { auth, db } from "../firebase";
import {
  deleteUser,
  sendEmailVerification,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { useHistory } from "react-router-dom";
import {
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  collection,
  where,
  query,
  serverTimestamp,
} from "@firebase/firestore";
import { GuidGenerator } from "../Util/Helpers/GuidGenerator";
import { TotalCostsGrossMarginAnnualSales } from "./TotalCostsGrossMarginAnnualSales";
import { AmountsFixedCostPerYear } from "./AmountsFixedCostPerYear";
import { TotalYearGrossMarginPerYear } from "./TotalYearGrossMarginPerYear";
import { TotalMeasuredCostsPerYear } from "./TotalMeasuredCostsPerYear";
import { GetMonthlySalaryPerYear } from "./GetMonthlySalaryPerYear";
import { CalculationYear1 } from "./CalculationYear1";
import { CalculationYear2 } from "./CalculationYear2";
import { CalculationYear3 } from "./CalculationYear3";

//redux
import { useDispatch, useSelector } from "react-redux";
import { setUserId } from "../Redux/Actions/userId";
import { getSaveMessage } from "../Redux/Actions/saveMessage";
import usePremiumStatus from "../stripe/usePremiumStatus";

//create context
const AuthContext = createContext();

//export context for use in app
export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
  //#region useStates and global useSelectors
  const [currentUser, setCurrentUser] = useState();
  //loading for currentUser
  const [loading, setLoading] = useState(true);
  //calculating gives time for calculations in salarycalculation
  const [calculating, setCalculating] = useState(false);
  //below holds data for mixer, fixedCost and measures
  const [data, setData] = useState();
  const [fixedCostData, setFixedCostData] = useState();
  const [measuresData, setMeasuresData] = useState();
  //holds state for companyForm to use in salarycalculation
  const [salaryCalculationData, setSalaryCalculationData] = useState();
  //holds userData, used for checking validUntil variable
  const [usersData, setUsersData] = useState();
  //holds subscription id from active stripe subscription
  const [activeStripeSubscriptionId, setActiveStripeSubscriptionId] =
    useState();
  //subscriptiondata
  const [subscriptionData, setSubscriptionData] = useState();
  const [isTrialAllowed, setIsTrialAllowed] = useState(true);
  //stripe active status
  const [subscriptionActiveStatus, setSubscriptionActiveStatus] = useState();
  const [isPremium, setIsPremium] = useState(false);
  //redirect
  const history = useHistory();
  //redux
  const dispatch = useDispatch();
  //keeps track of user id, makes sure userId exists
  const showId = useSelector((state) => state.userId);
  //keeps track of year selected
  const activeYear = useSelector((state) => state.year);
  //keeps track of companyForm selected
  const activeCompany = useSelector((state) => state.companyForm);
  //keeps track of currentpage
  const currentPage = useSelector((state) => state.activePage);
  //set globalstate to local variable
  const currentId = showId.uid;
  //set user id for stripe account
  // const stripeId = usersData.stripeId
  // //firestore instance
  // const firestore = firebase.firestore()
  //#endregion useStates and global useSelectors
  //#region onAuthStateChanged
  //listener that listenes for change in auth, unsubscribe to stop listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUser(user);
        //set user id to global state
        dispatch(setUserId(user.uid));
        //on render method verifies if user exists, then setLoading false
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [dispatch]);
  //#endregion onAuthStateChanged
  const getFirebaseRole = usePremiumStatus(currentUser);
  useEffect(() => {
    setIsPremium(getFirebaseRole);
  }, [getFirebaseRole]);
  //#region get all data
  //keeps track of companyForm per year to display correct monthly salary in dashboard measure
  useEffect(() => {
    try {
      if (currentId && currentUser) {
        const querySalaryCalculation = query(
          collection(db, "Lonekalkyl"),
          where("user", "==", `${currentId}`),
        );

        const unsubscribeSalaryCalculation = onSnapshot(
          querySalaryCalculation,
          (snapshot) => {
            //remove later, console logs changes in snapshot
            // snapshot.docChanges().forEach((change) => {
            //   if (change.type === "added") {
            //     console.log("added in docChange Åtgärder=> ", change.doc.data());
            //   }
            //   if (change.type === "modified") {
            //     console.log("modded in docChange Åtgärder=> ", change.doc.data());
            //   }
            //   if (change.type === "removed") {
            //     console.log("removed in docChange Åtgärder=> ", change.doc.data());
            //   }
            // });
            //set data in documentId.
            setSalaryCalculationData(
              snapshot.docs.map((doc) => ({
                ...doc.data(),
                user: currentId,
              })),
            );
          },
          (error) => {},
        );
        return () => {
          unsubscribeSalaryCalculation();
        };
      }
    } catch (err) {}
  }, [currentId, currentUser]);
  useEffect(() => {
    try {
      let isMounted = true;
      if (isMounted) {
        if (currentId && currentUser) {
          if (subscriptionActiveStatus) {
            async function getData() {
              const salariesDocRef = doc(
                db,
                "users",
                currentId,
                "Lonsamhetsmodul",
                "Produktmixern",
              );

              try {
                const docSnap = await getDoc(salariesDocRef);
                if (docSnap.exists()) {
                  const data = docSnap.data();

                  setData(data); // Return the data if the document exists
                } else {
                  return null; // Return null if the document does not exist
                }
              } catch (error) {
                throw error;
              }
            }
            async function getFixedCostData() {
              const fixedCostDocRef = doc(
                db,
                "users",
                currentId,
                "Lonsamhetsmodul",
                "Fastakostnader",
              );
              try {
                const docSnap = await getDoc(fixedCostDocRef);
                if (docSnap.exists()) {
                  const data = docSnap.data();

                  setFixedCostData(data); // Return the data if the document exists
                } else {
                  return null; // Return null if the document does not exist
                }
              } catch (error) {
                console.error("Error getting document: ", error);
                throw error;
              }
            }
            async function getMeasuresData() {
              const measuresDocRef = doc(
                db,
                "users",
                currentId,
                "Lonsamhetsmodul",
                "Åtgärder",
              );
              try {
                const docSnap = await getDoc(measuresDocRef);
                if (docSnap.exists()) {
                  const data = docSnap.data();

                  setMeasuresData(data); // Return the data if the document exists
                } else {
                  return null; // Return null if the document does not exist
                }
              } catch (error) {
                console.error("Error getting document: ", error);
                throw error;
              }
            }
            getData();
            getFixedCostData();
            getMeasuresData();
          }
          async function getUsersData() {
            const usersDocRef = doc(db, "users", currentId);
            try {
              const docSnap = await getDoc(usersDocRef);
              if (docSnap.exists()) {
                setUsersData(docSnap.data());
              } else {
                return null; // Return null if the document does not exist
              }
            } catch (error) {
              console.error("Error getting document: ", error);
              throw error;
            }
          }

          getUsersData();
        }
      }
      return () => {
        isMounted = false;
      };
    } catch (err) {}
  }, [currentId, subscriptionActiveStatus, currentUser]);

  //#endregion get all data

  //#region alert when refreshing or leaving page
  //if user refreshes page or leaves site, show warning to save before or lose changes
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      if (subscriptionActiveStatus) {
        if (
          currentPage === "Produktmixern" ||
          currentPage === "Fastakostnader" ||
          currentPage === "Åtgärder" ||
          currentPage === "Lonekalkyl"
        ) {
          window.addEventListener("beforeunload", alertUser);
          window.addEventListener("unload", handleLeave);
          return () => {
            window.removeEventListener("beforeunload", alertUser);
            window.removeEventListener("unload", handleLeave);
          };
        }
      }
      return () => {
        isMounted = false;
      };
    }
  }, [currentPage, subscriptionActiveStatus]);

  const alertUser = (e) => {
    e.preventDefault();
    e.returnValue = "";
  };
  const handleLeave = async () => {};
  //#endregion alert when refreshing or leaving page

  //#region post to local data
  async function postLocalData(object) {
    //save  object onclick
    if (currentPage === "Produktmixern") {
      const payload = {
        id: GuidGenerator(),
        user: currentId,
        year: activeYear.year,
        timeStamp: Date(),
        object,
      };
      if (data) {
        const newList = [...data.data];
        const updatedList = [...newList.concat(payload)];
        setData({ data: updatedList });
      }
    }
    if (currentPage === "Fastakostnader") {
      const payload = {
        id: GuidGenerator(),
        user: currentId,
        year: activeYear.year,
        timeStamp: Date(),
        object,
      };
      if (fixedCostData) {
        const newList = [...fixedCostData.fixedCostData];
        const updatedList = [...newList.concat(payload)];
        setFixedCostData({ fixedCostData: updatedList });
      }
    }
    if (currentPage === "Åtgärder") {
      const payload = {
        id: GuidGenerator(),
        user: currentId,
        timeStamp: Date(),
        object,
      };
      if (measuresData) {
        const newList = [...measuresData.measuresData];
        const updatedList = [...newList.concat(payload)];
        setMeasuresData({ measuresData: updatedList });
      }
    }
  }

  //#region add rows if no data
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      if (!data) {
        const data = [
          {
            id: GuidGenerator(),
            user: currentId,
            year: activeYear.year,
            timeStamp: Date(),
            object: {
              category: "",
              product: "",
              price: "0",
              variableCost: "0",
              volumeYear: "0",
              workHours: "0",
            },
          },
          {
            id: GuidGenerator(),
            user: currentId,
            year: activeYear.year,
            timeStamp: Date(),
            object: {
              category: "",
              product: "",
              price: "0",
              variableCost: "0",
              volumeYear: "0",
              workHours: "0",
            },
          },
        ];
        setData({ data: data });
      }

      if (!fixedCostData) {
        const data = [
          {
            id: GuidGenerator(),
            user: currentId,
            year: activeYear.year,
            timeStamp: Date(),
            object: {
              category: "",
              amount: "0",
            },
          },
          {
            id: GuidGenerator(),
            user: currentId,
            year: activeYear.year,
            timeStamp: Date(),
            object: {
              category: "",
              amount: "0",
            },
          },
        ];
        setFixedCostData({ fixedCostData: data });
      }
      if (!measuresData) {
        const data = [
          {
            id: GuidGenerator(),
            user: currentId,
            timeStamp: Date(),
            object: {
              costs1: 0,
              costs2: 0,
              costs3: 0,
              enddate: "",
              hours1: 0,
              hours2: 0,
              hours3: 0,
              measureName: "",
              priority: "3",
              responsible: "",
              startdate: "",
            },
          },
          {
            id: GuidGenerator(),
            user: currentId,
            timeStamp: Date(),
            object: {
              costs1: 0,
              costs2: 0,
              costs3: 0,
              enddate: "",
              hours1: 0,
              hours2: 0,
              hours3: 0,
              measureName: "",
              priority: "3",
              responsible: "",
              startdate: "",
            },
          },
        ];
        setMeasuresData({ measuresData: data });
      }
    }
    return () => {
      isMounted = false;
    };
  }, [activeYear.year, currentId, data, fixedCostData, measuresData]);
  //#endregion add rows if no data
  //#endregion post to local data

  //#region edit local data
  function editLocalData(e, idCurrentRow) {
    if (currentPage === "Produktmixern") {
      let updatedList = data.data.map((item) => {
        if (item.id === idCurrentRow) {
          const object = {
            ...item.object,
            [e.target.name]:
              e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1),
          };
          return { ...item, object };
        }
        return item;
      });
      setData({ data: updatedList });
    }
    if (currentPage === "Fastakostnader") {
      let updatedList = fixedCostData.fixedCostData.map((item) => {
        if (item.id === idCurrentRow) {
          const object = {
            ...item.object,
            [e.target.name]:
              e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1),
          };
          return { ...item, object };
        }
        return item;
      });
      setFixedCostData({ fixedCostData: updatedList });
    }
    if (currentPage === "Åtgärder") {
      let updatedList = measuresData.measuresData.map((item) => {
        if (item.id === idCurrentRow) {
          const object = {
            ...item.object,
            [e.target.name]:
              e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1),
          };
          return { ...item, object };
        }
        return item;
      });
      setMeasuresData({ measuresData: updatedList });
    }
  }
  // handle numberdata
  function editLocalNumberData(e, idCurrentRow) {
    if (currentPage === "Produktmixern") {
      //"" in input must return a 0 for calculations to generate result
      if (e.target.value === "") {
        e.target.value = 0;
      }
      let updatedList = data.data.map((item) => {
        if (item.id === idCurrentRow) {
          const object = {
            ...item.object,
            [e.target.name]: parseFloat(e.target.value),
          };
          return { ...item, object };
        }
        return item;
      });
      setData({ data: updatedList });
    }
    if (currentPage === "Fastakostnader") {
      //"" in input must return a 0 for calculations to generate result
      if (e.target.value === "") {
        e.target.value = 0;
      }
      let updatedList = fixedCostData.fixedCostData.map((item) => {
        if (item.id === idCurrentRow) {
          const object = {
            ...item.object,
            [e.target.name]: parseInt(e.target.value),
          };
          return { ...item, object };
        }
        return item;
      });
      setFixedCostData({ fixedCostData: updatedList });
    }
    if (currentPage === "Åtgärder") {
      //"" in input must return a 0 for calculations to generate result
      if (e.target.value === "") {
        e.target.value = 0;
      }
      let updatedList = measuresData.measuresData.map((item) => {
        if (item.id === idCurrentRow) {
          const object = {
            ...item.object,
            [e.target.name]: parseInt(e.target.value),
          };
          return { ...item, object };
        }
        return item;
      });
      setMeasuresData({ measuresData: updatedList });
    }
  }
  //#endregion edit local data

  //#region save localdata to database
  //save companyform to database
  async function editCompanyForm(activeCompany) {
    if (currentId) {
      const docRef = doc(db, `Lonekalkyl/${currentId}`);
      await setDoc(
        docRef,
        {
          object: { activeCompany },
          user: currentId,
        },
        { merge: true },
      );
    }
  }
  //save all local lists to db on one click, nested functions
  const saveMessage = useCallback(() => {
    if (currentId) {
      dispatch(getSaveMessage(`Din data har blivit sparad.`));

      // Remove save message after 2 sec
      const delayEditData = setTimeout(() => {
        dispatch(getSaveMessage(""));
      }, 2000);

      // Clear the timeout on component unmount or when currentId changes
      return () => clearTimeout(delayEditData);
    }
  }, [dispatch, currentId]);

  function saveToDatabase() {
    try {
      async function saveToMixer() {
        const docRef = doc(
          db,
          "users",
          currentId,
          "Lonsamhetsmodul",
          "Produktmixern",
        );
        try {
          await setDoc(docRef, { ...data }, { merge: true });
          saveMessage();
        } catch (error) {
          console.error("Error writing document: ", error);
        }
      }

      async function saveToFixedCost() {
        const docRef = doc(
          db,
          "users",
          currentId,
          "Lonsamhetsmodul",
          "Fastakostnader",
        );
        try {
          await setDoc(docRef, { ...fixedCostData }, { merge: true });
          saveMessage();
        } catch (error) {
          console.error("Error writing document: ", error);
        }
      }

      async function saveToMeasures() {
        const docRef = doc(
          db,
          "users",
          currentId,
          "Lonsamhetsmodul",
          "Åtgärder",
        );
        try {
          await setDoc(docRef, { ...measuresData }, { merge: true });
          saveMessage();
        } catch (error) {
          console.error("Error writing document: ", error);
        }
      }
      saveToMixer();
      saveToFixedCost();
      saveToMeasures();
    } catch {}
  }

  //auto save every 5 min
  useEffect(() => {
    let isMounted = true;
    if (isMounted && currentId && currentUser && subscriptionActiveStatus) {
      const delayEditData = setTimeout(async () => {
        try {
          const docRef = doc(
            db,
            "users",
            currentId,
            "Lonsamhetsmodul",
            "Produktmixern",
          );
          await setDoc(db, docRef, { ...data }, { merge: true });
          const fixedCostDocRef = doc(
            db,
            "users",
            currentId,
            "Lonsamhetsmodul",
            "Fastakostnader",
          );
          await setDoc(fixedCostDocRef, { ...fixedCostData }, { merge: true });
          const measuresDocRef = doc(
            db,
            "users",
            currentId,
            "Lonsamhetsmodul",
            "Åtgärder",
          );
          await setDoc(measuresDocRef, { ...measuresData }, { merge: true });
          saveMessage();
        } catch (error) {
          console.error("Error writing document: ", error);
        }
      }, 300000); // 5 minutes

      return () => {
        clearTimeout(delayEditData);
      };
    }

    return () => {
      isMounted = false;
    };
  }, [
    currentId,
    currentUser,
    data,
    fixedCostData,
    measuresData,
    saveMessage,
    subscriptionActiveStatus,
  ]);
  //#endregion save localdata to database
  //#region handle delete
  //delete from local list, takes in id then filters and saves new list to state
  function deleteLocalData(id) {
    if (currentPage === "Produktmixern") {
      const newList = [...data.data];
      const deleteId = newList.filter((i) => i.id !== id);
      setData({ data: deleteId });
    }
    if (currentPage === "Fastakostnader") {
      const newList = [...fixedCostData.fixedCostData];
      const deleteId = newList.filter((i) => i.id !== id);
      setFixedCostData({ fixedCostData: deleteId });
    }
    if (currentPage === "Åtgärder") {
      const newList = [...measuresData.measuresData];
      const deleteId = newList.filter((i) => i.id !== id);
      setMeasuresData({ measuresData: deleteId });
    }
  }
  //delete user
  async function deleteUserFromDatabase() {
    const user = auth.currentUser;
    if (!user) {
      console.error("No user is currently signed in");
      return;
    }

    try {
      await handleDeleteUserData();
      await deleteUser(user);
      history.push("/login");
      alert("Användare borttagen");
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  }
  //delete all user data, firestore cannot delete subcollections this deletes per doc
  async function handleDeleteUserData() {
    try {
      const calcDocRef = doc(db, "calc", currentId);
      await deleteDoc(calcDocRef);

      //delete subcollections have to be done manually: try this later if subcollections are left after deleting user (subcollections are in the user collection so they should be deleted when user is deleted)
      // const subCollections = ['subCollection1', 'subCollection2']; // Add your subcollections here

      // for (const subCollection of subCollections) {
      //   const subCollectionRef = collection(db, 'calc', currentId, subCollection);
      //   const subCollectionQuery = query(subCollectionRef);
      //   const subCollectionSnapshot = await getDocs(subCollectionQuery);

      //   for (const doc of subCollectionSnapshot.docs) {
      //     await deleteDoc(doc.ref);
      //   }
      // }
    } catch (error) {
      console.error("Error deleting user data:", error);
    }
  }

  //#endregion handle delete
  //#region signup, valid user check, login and logout
  async function signUp(name, email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;
      await sendEmailVerification(user);

      // Define userDocRef here
      const userDocRef = doc(db, "users", user.uid);
      await setDoc(userDocRef, {
        email: email,
        name: name,
        displayName: name,
        uid: user.uid,
        createdAt: serverTimestamp(),
      });

      alert("Vänligen titta i din mejl och följ länken för att verifiera dig.");
      return { user };
    } catch (error) {
      console.error("Error signing up: ", error);
      throw error;
    }
  }

  //send verificationEmail
  function sendVerificationEmail() {
    sendEmailVerification(auth.currentUser);
    alert(
      "Länk skickad till er mejl, var vänlig logga in igen efter verifiering av er mejl.",
    );
  }

  //get subscription id from active stripe subscription
  useEffect(() => {
    if (currentId && currentUser) {
      async function getSubscriptionData() {
        const subscriptionsRef = collection(
          db,
          "users",
          currentId,
          "subscriptions",
        );
        const q = query(
          subscriptionsRef,
          where("status", "in", ["trialing", "active"]),
        );
        try {
          const querySnapshot = await getDocs(subscriptionsRef);
          querySnapshot.forEach((doc) => {
            const data = doc.data();

            if (data.status !== "active") {
              // Check for specific statuses
              if (data.status === "canceled") {
                setIsTrialAllowed(false); // No trial allowed if canceled or used previously
              } else {
                setIsTrialAllowed(true); // Allow trial for other statuses (excluding "active")
              }
            }
          });
        } catch (error) {}

        try {
          const querySnapshot = await getDocs(q);
          if (querySnapshot.empty) {
            setSubscriptionActiveStatus(false);
            return null;
          }

          const products = {};
          let subscriptionId = null;
          let activeStatus = false;

          querySnapshot.forEach((doc) => {
            const data = doc.data();
            products[doc.id] = data;
            subscriptionId = doc.id;

            if (data.items && data.items.length > 0) {
              activeStatus = data.items[0].price.product.active;
            }
          });
          if (subscriptionId) {
            setActiveStripeSubscriptionId(subscriptionId);
          }
          if (Object.keys(products).length > 0) {
            setSubscriptionData(products);
          }
          setSubscriptionActiveStatus(activeStatus);
        } catch (error) {
          console.error("Error getting documents: ", error);
        }
      }

      getSubscriptionData();
    }
  }, [
    currentId,
    currentUser,
    setSubscriptionData,
    setActiveStripeSubscriptionId,
    setSubscriptionActiveStatus,
    usersData,
    isTrialAllowed,
  ]);

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;
      return user;
    } catch (error) {
      throw error;
    }
  }

  function logout() {
    return signOut(auth);
  }
  // reset password takes in email then sends to firebase function
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email)
      .then(function () {
        alert("Vänligen titta i din mail");
      })
      .catch(function (e) {});
  };

  //#endregion signup, login and logout
  //#region calculations for salaryCalculation
  //all calculations in authContext.js for fastest response
  //calculation needs to run for all years since dashboard in "Åtgärder" shows monthly salary for all years.
  //#region add all amounts in mixer and fixed cost, filter by year.

  TotalCostsGrossMarginAnnualSales(fixedCostData, activeYear, dispatch, data);
  //#endregion add all amounts of mixer and fixed cost filter by year.

  //#region add all amounts in "Fasta kostnader", per year
  AmountsFixedCostPerYear(fixedCostData, dispatch);

  //#endregion add all amounts in fixed cost, per year
  TotalYearGrossMarginPerYear(data, dispatch);

  //#region add all costs and hours per year in "Åtgärder", all presented in measures dashboard
  TotalMeasuredCostsPerYear(measuresData, dispatch);
  //#endregion add all costs and hours per year in measures, all presented in measures dashboard

  //#region selectors for fixed cost, year gross margin and measures per year. Also selectors for percent and cut off values

  const totalFixedCost = useSelector((state) => state.totalFixedCost);
  const totalFixedCostPerYear = useSelector(
    (state) => state.TotalFixedCostPerYear,
  );
  const totalYearGrossMargin = useSelector(
    (state) => state.totalYearGrossMargin,
  );
  const totalYearGrossMarginPerYear = useSelector(
    (state) => state.TotalYearGrossMarginPerYear,
  );

  const totalMeasuredCostsPerYear = useSelector(
    (state) => state.TotalMeasuredCostsYear,
  );

  const standardTaxRelief = useSelector(
    (state) => state.salaryCalculation.standardTaxRelief,
  );
  const stateTaxPercent = useSelector(
    (state) => state.salaryCalculation.stateTaxPercent,
  );
  const generalPensionCutOffValue = useSelector(
    (state) => state.salaryCalculation.generalPensionCutOffValue,
  );
  const generalPensionPercent = useSelector(
    (state) => state.salaryCalculation.generalPensionPercent,
  );
  const templatePayrollPercent = useSelector(
    (state) => state.salaryCalculation.templatePayrollPercent,
  );
  const localTaxesPercent = useSelector(
    (state) => state.salaryCalculation.localTaxesPercent,
  );
  const stateTaxCutOfValue = useSelector(
    (state) => state.salaryCalculation.stateTaxCutOfValue,
  );
  const payrollTaxPercent = useSelector(
    (state) => state.salaryCalculation.payrollTaxPercent,
  );
  const funeralFeesPercent = useSelector(
    (state) => state.salaryCalculation.funeralFeesPercent,
  );
  const earnedIncomeTaxCutOffValue = useSelector(
    (state) => state.salaryCalculation.earnedIncomeTaxCutOffValue,
  );
  const earnedIncomeTaxPercent = useSelector(
    (state) => state.salaryCalculation.earnedIncomeTaxPercent,
  );
  const generalPayrollTaxPercent = useSelector(
    (state) => state.salaryCalculation.generalPayrollTaxPercent,
  );
  const nonTaxableIncomeLimitNegativeNumber = useSelector(
    (state) => state.salaryCalculation.nonTaxableIncomeLimit,
  );
  //math.abs switch number to positive
  const nonTaxableIncomeLimit = Math.abs(nonTaxableIncomeLimitNegativeNumber);
  //#endregion selectors for fixed cost, year gross margin and measures per year. Also selectors for percent and cut off values

  GetMonthlySalaryPerYear(
    dispatch,
    calculating,
    templatePayrollPercent,
    standardTaxRelief,
    localTaxesPercent,
    payrollTaxPercent,
    generalPensionPercent,
    generalPensionCutOffValue,
    funeralFeesPercent,
    earnedIncomeTaxCutOffValue,
    earnedIncomeTaxPercent,
    generalPayrollTaxPercent,
    activeCompany,
    activeYear,
    totalFixedCost,
    totalYearGrossMargin,
    totalMeasuredCostsPerYear,
    nonTaxableIncomeLimit,
    stateTaxCutOfValue,
    stateTaxPercent,
    setCalculating,
  );

  //#region calculations to get monthly salary in dashboard year1
  CalculationYear1(
    dispatch,
    calculating,
    templatePayrollPercent,
    standardTaxRelief,
    localTaxesPercent,
    payrollTaxPercent,
    generalPensionPercent,
    generalPensionCutOffValue,
    funeralFeesPercent,
    earnedIncomeTaxCutOffValue,
    earnedIncomeTaxPercent,
    generalPayrollTaxPercent,
    activeCompany,
    activeYear.year,
    totalFixedCost,
    totalYearGrossMargin,
    totalMeasuredCostsPerYear,
    nonTaxableIncomeLimit,
    stateTaxCutOfValue,
    stateTaxPercent,
    totalYearGrossMarginPerYear,
    totalFixedCostPerYear,
    salaryCalculationData,
    setCalculating,
  );
  //#endregion calculations to get monthly salary in dashboard year1

  //#region calculations to get monthly salary in dashboard year2
  CalculationYear2(
    dispatch,
    calculating,
    templatePayrollPercent,
    standardTaxRelief,
    localTaxesPercent,
    payrollTaxPercent,
    generalPensionPercent,
    generalPensionCutOffValue,
    funeralFeesPercent,
    earnedIncomeTaxCutOffValue,
    earnedIncomeTaxPercent,
    generalPayrollTaxPercent,
    activeCompany,
    activeYear.year,
    totalFixedCost,
    totalYearGrossMargin,
    totalMeasuredCostsPerYear,
    nonTaxableIncomeLimit,
    stateTaxCutOfValue,
    stateTaxPercent,
    totalYearGrossMarginPerYear,
    totalFixedCostPerYear,
    salaryCalculationData,
    setCalculating,
  );

  //#endregion calculations to get monthly salary in dashboard year2

  //#region calculations to get monthly salary in dashboard year3
  CalculationYear3(
    dispatch,
    calculating,
    templatePayrollPercent,
    standardTaxRelief,
    localTaxesPercent,
    payrollTaxPercent,
    generalPensionPercent,
    generalPensionCutOffValue,
    funeralFeesPercent,
    earnedIncomeTaxCutOffValue,
    earnedIncomeTaxPercent,
    generalPayrollTaxPercent,
    activeCompany,
    activeYear.year,
    totalFixedCost,
    totalYearGrossMargin,
    totalMeasuredCostsPerYear,
    nonTaxableIncomeLimit,
    stateTaxCutOfValue,
    stateTaxPercent,
    totalYearGrossMarginPerYear,
    totalFixedCostPerYear,
    salaryCalculationData,
    setCalculating,
  );

  //#endregion calculations to get monthly salary in dashboard year3

  //#endregion calculations for salaryCalculation
  const value = {
    currentUser,
    isPremium,
    data,
    isTrialAllowed,
    setData,
    fixedCostData,
    setFixedCostData,
    salaryCalculationData,
    measuresData,
    setMeasuresData,
    signUp,
    login,
    logout,
    editCompanyForm,
    editLocalData,
    editLocalNumberData,
    saveToDatabase,
    postLocalData,
    deleteLocalData,
    resetPassword,
    usersData,
    deleteUserFromDatabase,
    handleDeleteUserData,
    activeStripeSubscriptionId,
    subscriptionData,
    subscriptionActiveStatus,
    sendVerificationEmail,
  };
  return (
    <AuthContext.Provider value={value}>
      {/* do not render application if loading, gives firebase time to verify user from local storage, or else user will be null */}
      {!loading && children}
    </AuthContext.Provider>
  );
}
